/**
 * TimeClockContext.tsx
 * 
 * Backward-compatible context wrapper over standalone React Query hooks.
 * All standard data fetching and mutations are delegated to hooks/useTimeClockQueries.ts.
 * 
 * Existing consumers can continue using `useTimeClockCtx()` without changes.
 */
import React, { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import { useDemoVisualActivo } from '../hooks/useDemoVisual';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { useWorkspace } from './WorkspaceContext';
import { TimeLog, TeamMember, Crew } from '../types';
import { fetchTimeLogsRange, fetchActiveTimeLogs, fetchOpenTimeLogs } from '../services/database';
import type { TransferShiftArgs } from '../services/database';
import { computeNetMinutes } from '../components/workforce/timesheet-utils';
import {
    useTeamMembers, useCrews,
    useAddTeamMember, useUpdateTeamMember, useRemoveTeamMember,
    useAddCrew, useUpdateCrew, useDeleteCrew,
    useAddTimeLog, useUpdateTimeLog, useDeleteTimeLog, useTransferShift
} from '../hooks/useTimeClockQueries';

interface TimeClockContextType {
    activeTimeLogs: TimeLog[];
    teamMembers: TeamMember[];
    fetchTimeLogs: (daysBack?: number) => Promise<TimeLog[]>;
    /** Force an immediate re-read of who is on the clock. Clock actions call
     *  this themselves; exposed for screens that need to refresh on demand. */
    refreshActiveLogs: () => Promise<void>;
    clockIn: (data: Omit<TimeLog, 'id' | 'clockOut'>) => Promise<void>;
    clockOut: (logId: string, data: {
        clockOut: string;
        locationOut?: { lat: number, lng: number };
        clockOutPhotoUrl?: string;
        clockOutOffSite?: boolean;
        clockOutDistanceFromSiteFt?: number;
    }) => Promise<void>;
    /** Mueve el turno en curso a otro destino sin fichar salida: cierra el
     *  tramo abierto y abre el siguiente en la misma transacción del servidor.
     *  Devuelve el tramo NUEVO. */
    transferTo: (args: TransferShiftArgs) => Promise<TimeLog>;
    startLunch: (logId: string) => Promise<void>;
    endLunch: (logId: string) => Promise<void>;
    updateTimeLog: (id: string, updates: Partial<TimeLog>) => Promise<void>;
    deleteTimeLog: (id: string) => Promise<void>;

    addTeamMember: (member: TeamMember) => Promise<void>;
    updateTeamMember: (id: string, updates: Partial<TeamMember>) => Promise<void>;
    removeTeamMember: (id: string) => Promise<void>;

    crews: Crew[];
    addCrew: (crew: Omit<Crew, 'id'>) => Promise<void>;
    updateCrew: (id: string, updates: Partial<Crew>) => Promise<void>;
    deleteCrew: (id: string) => Promise<void>;
}

const TimeClockContext = createContext<TimeClockContextType | undefined>(undefined);

export const useTimeClockCtx = () => {
    const context = useContext(TimeClockContext);
    if (!context) {
        throw new Error('useTimeClockCtx must be used within a TimeClockProvider');
    }
    return context;
};

export const TimeClockProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { isAuthorized, currentUser, userRole } = useAuth();
    const { activeWorkspace, status } = useWorkspace();
    const qc = useQueryClient();

    /* El taller que se pasa a los lectores del reloj, y si ya se puede leer.
       Los tres (`fetchActiveTimeLogs`, `fetchOpenTimeLogs`, `fetchTimeLogsRange`)
       piden el id como parámetro OBLIGATORIO: antes lo sacaban por su cuenta de
       `getWorkspaceId()`, que lee el localStorage, y este efecto corre ANTES de
       que WorkspaceContext escriba ahí el taller nuevo — la escucha se quedaba
       clavada en el anterior sin que nada la volviera a montar. */
    const wsId = activeWorkspace?.id || 'default';
    const tallerListo = status === 'resolved' && wsId !== 'default';

    // Active time logs use realtime subscription (clock-status is the core UX of this view).
    // Whenever the channel fires, we ALSO invalidate the per-project time_logs cache so the
    // ProjectDetail Vitals bar + Activity feed stay in sync without manual refresh.
    const [realActiveLogs, setActiveTimeLogs] = useState<TimeLog[]>([]);

    useEffect(() => {
        if (!isAuthorized || !currentUser || !tallerListo) {
            setActiveTimeLogs([]);
            return;
        }

        const unsub = fetchActiveTimeLogs(
            userRole,
            currentUser.uid,
            (logs) => {
                setActiveTimeLogs(logs);
                // Per-project queries (useProjectLogs) are keyed by ['time-logs', wsId, projectId].
                // Invalidating the prefix refreshes all open project views.
                qc.invalidateQueries({ queryKey: ['time-logs'] });
            },
            wsId,
        );

        return () => unsub();
        /* `wsId` va en las dependencias Y como argumento, y hacen falta las dos
           cosas. El canal se ata al taller del momento de suscribirse
           (`active_logs:<wsId>`, con filtro `workspace_id=eq.<wsId>`): la
           dependencia hace que se vuelva a montar al cambiar de taller, y el
           argumento hace que se monte con el taller NUEVO. Con la dependencia
           sola —que es como estaba— el lector releía el id del localStorage,
           donde WorkspaceContext todavía no lo había escrito, y la escucha se
           remontaba idéntica sobre el taller anterior. */
    }, [isAuthorized, currentUser?.uid, userRole, wsId, tallerListo, qc]);

    // --- QUERIES (delegated to standalone hooks) ---
    const { data: teamMembers = [] } = useTeamMembers();
    const { data: realCrews = [] } = useCrews();

    /* Relleno visual de mentira para capturas — Ctrl/⌘ + Shift + D.
       Va aquí y no en WorkforceView porque TeamActivityFeed lee del contexto
       directamente: sembrarlo en la vista dejaría el panel derecho vacío
       mientras las tarjetas de arriba decían que hay gente trabajando.
       Vive en estado de React: recargar lo quita y NADA toca la base. */
    const demoVisual = useDemoVisualActivo();
    const [demoReloj, setDemoReloj] = useState<{ turnos: TimeLog[]; cuadrillas: Crew[] } | null>(null);

    const yaSembrado = useRef(false);
    useEffect(() => {
        // La rama entera desaparece del build: en producción `DEV` es `false`.
        if (!import.meta.env.DEV) return;
        if (!demoVisual || yaSembrado.current) return;
        yaSembrado.current = true;
        void import('../hooks/demoRelojDatos').then(d => {
            setDemoReloj({ turnos: d.DEMO_TURNOS, cuadrillas: d.DEMO_CUADRILLAS_RELOJ });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [demoVisual]);

    // Se añaden a lo real, no lo sustituyen.
    const activeTimeLogs = demoReloj ? [...realActiveLogs, ...demoReloj.turnos] : realActiveLogs;
    const crews = demoReloj ? [...realCrews, ...demoReloj.cuadrillas] : realCrews;

    // --- MUTATIONS (delegated to standalone hooks) ---
    const addTeamMemberMut = useAddTeamMember();
    const updateTeamMemberMut = useUpdateTeamMember();
    const removeTeamMemberMut = useRemoveTeamMember();
    const addCrewMut = useAddCrew();
    const updateCrewMut = useUpdateCrew();
    const deleteCrewMut = useDeleteCrew();
    const addTimeLogMut = useAddTimeLog();
    const updateTimeLogMut = useUpdateTimeLog();
    const deleteTimeLogMut = useDeleteTimeLog();
    const transferShiftMut = useTransferShift();

    const fetchTimeLogs = async (daysBack = 30) => {
        // Sin taller resuelto no se consulta: el respaldo 'default' devolvería
        // cero turnos, que la pantalla lee como «nadie fichó».
        if (!tallerListo) return [];
        return fetchTimeLogsRange(daysBack, wsId);
    };

    /**
     * Pull the open-shift list again right now and refresh every dependent view.
     *
     * Every clock action calls this instead of trusting the realtime channel.
     * Realtime is still wired up and still helps (it's how OTHER people's
     * devices update), but it must never be the only path — if the table isn't
     * in the realtime publication, or the socket is down, or the worker is on
     * bad signal, the person who just clocked in would see nothing change and
     * reasonably conclude it failed.
     */
    const refreshActiveLogs = useCallback(async () => {
        if (!currentUser?.uid || !tallerListo) return;
        try {
            const logs = await fetchOpenTimeLogs(userRole, currentUser.uid, wsId);
            setActiveTimeLogs(logs);
        } catch (err) {
            // A failed refresh must not fail the clock action itself — the write
            // already succeeded, and realtime may still catch up.
            console.warn('[TimeClock] active-log refresh failed:', err);
        }
        // Timesheets, project hours, Clock Map and job costing all read time logs.
        qc.invalidateQueries({ queryKey: ['time-logs'] });
    }, [currentUser?.uid, userRole, wsId, tallerListo, qc]);

    const clockIn = async (data: Omit<TimeLog, 'id' | 'clockOut'>) => {
        await addTimeLogMut.mutateAsync(data);
        await refreshActiveLogs();
    };

    const clockOut = async (
        logId: string,
        data: {
            clockOut: string;
            locationOut?: { lat: number, lng: number };
            clockOutPhotoUrl?: string;
            clockOutOffSite?: boolean;
            clockOutDistanceFromSiteFt?: number;
        }
    ) => {
        // Turnos de mentira: existen solo en memoria para la captura. Intentar
        // cerrarlos mandaría un id inventado a la base y reventaría.
        if (logId.startsWith('demo-reloj')) {
            console.info('[TimeClock] demo shift — clock-out ignored, nothing to persist.');
            return;
        }

        let log = activeTimeLogs.find(t => t.id === logId);

        if (!log) {
            console.warn(`TimeLog with ID ${logId} not found in active logs during clockOut.`);
            return;
        }

        /* La cuenta del neto se hacía a mano aquí, y era una de las siete
           reglas de horas que no coincidían entre sí. Va por la fórmula
           compartida. El lonche que nadie cerró se da por terminado a la
           salida —igual que hacía este código— porque de otro modo el turno
           pagaría también el descanso.
           `Number.isFinite` protege lo que protegía el viejo `startTime > 0`:
           con un `clockIn` ilegible se guarda 0, no un NaN a la base. */
        const neto = computeNetMinutes(
            log.clockIn,
            data.clockOut,
            log.lunchStart,
            log.lunchEnd || data.clockOut,
        );

        await updateTimeLogMut.mutateAsync({
            id: logId,
            updates: {
                ...data,
                durationMinutes: Number.isFinite(neto) ? neto : 0,
                status: 'Working'
            } as any
        });
        await refreshActiveLogs();
    };

    const transferTo = async (args: TransferShiftArgs): Promise<TimeLog> => {
        // Los turnos de mentira de la captura viven solo en memoria: su id no es
        // un uuid, así que mandarlo a la RPC devuelve un 22P02 crudo que
        // `consolePatch` archivaría como error de producción.
        if (args.logId.startsWith('demo-reloj')) {
            throw new Error('DEMO_SHIFT_NOT_TRANSFERABLE');
        }
        const nuevo = await transferShiftMut.mutateAsync(args);
        // Igual que el resto de acciones del reloj: el refresco no se le deja a
        // realtime, que aquí es todavía más importante porque el traspaso cambia
        // la fila que la propia pantalla está enseñando.
        await refreshActiveLogs();
        return nuevo;
    };

    const startLunch = async (logId: string) => {
        await updateTimeLogMut.mutateAsync({
            id: logId,
            updates: { lunchStart: new Date().toISOString(), status: 'Lunch' } as any
        });
        await refreshActiveLogs();
    };

    const endLunch = async (logId: string) => {
        await updateTimeLogMut.mutateAsync({
            id: logId,
            updates: { lunchEnd: new Date().toISOString(), status: 'Working' } as any
        });
        await refreshActiveLogs();
    };

    const updateTimeLog = async (id: string, updates: Partial<TimeLog>) => {
        await updateTimeLogMut.mutateAsync({ id, updates });
    };

    const deleteTimeLog = async (id: string) => {
        await deleteTimeLogMut.mutateAsync(id);
    };

    const addTeamMember = async (member: TeamMember) => {
        await addTeamMemberMut.mutateAsync(member);
    };

    const updateTeamMember = async (id: string, updates: Partial<TeamMember>) => {
        await updateTeamMemberMut.mutateAsync({ id, updates });
    };

    const removeTeamMember = async (id: string) => {
        await removeTeamMemberMut.mutateAsync(id);
    };

    const addCrew = async (crew: Omit<Crew, 'id'>) => {
        await addCrewMut.mutateAsync(crew);
    };

    const updateCrew = async (id: string, updates: Partial<Crew>) => {
        await updateCrewMut.mutateAsync({ id, updates });
    };

    const deleteCrew = async (id: string) => {
        await deleteCrewMut.mutateAsync(id);
    };

    return (
        <TimeClockContext.Provider value={{
            activeTimeLogs, teamMembers, crews, fetchTimeLogs, refreshActiveLogs, clockIn, clockOut, transferTo, startLunch, endLunch, updateTimeLog, deleteTimeLog,
            addTeamMember, updateTeamMember, removeTeamMember, addCrew, updateCrew, deleteCrew
        }}>
            {children}
        </TimeClockContext.Provider>
    );
};
